import type { DataListRow } from '@arritech/ui/DataList/DataList.model';

/**
 * Ephemeral DataList selection actions.
 *
 * These values are intentionally kept in-memory only and are not persisted
 * to localStorage or synced to user preferences.
 */
export const selectionMemoryActions = {
	/**
	 * Returns selected rows for a DataList key.
	 */
	getDataListSelection(this: any, key: string): DataListRow[] {
		if (!key) {
			return [];
		}

		const selection = this.dataListSelectionMemory[key];
		return Array.isArray(selection) ? selection : [];
	},

	/**
	 * Returns whether the selection panel is open for a DataList key.
	 */
	getDataListSelectionPanelOpen(this: any, key: string): boolean {
		if (!key) {
			return false;
		}

		return Boolean(this.dataListSelectionPanelMemory[key]);
	},

	/**
	 * Stores selected rows for a DataList key.
	 */
	setDataListSelection(this: any, key: string, rows: DataListRow[]): void {
		if (!key) {
			return;
		}

		const normalizedRows = (rows || []).filter(
			(row): row is DataListRow =>
				Boolean(row) && typeof row === 'object' && !Array.isArray(row)
		);

		this.dataListSelectionMemory = {
			...this.dataListSelectionMemory,
			[key]: normalizedRows,
		};
	},

	/**
	 * Stores selection panel open state for a DataList key.
	 */
	setDataListSelectionPanelOpen(this: any, key: string, isOpen: boolean): void {
		if (!key) {
			return;
		}

		this.dataListSelectionPanelMemory = {
			...this.dataListSelectionPanelMemory,
			[key]: Boolean(isOpen),
		};
	},

	/**
	 * Clears selection rows and selection panel state for a DataList key.
	 */
	clearDataListSelection(this: any, key: string): void {
		if (!key) {
			return;
		}

		let hasUpdatedMemory = false;
		const next = { ...this.dataListSelectionMemory };
		if (next[key]) {
			delete next[key];
			hasUpdatedMemory = true;
		}
		if (hasUpdatedMemory) {
			this.dataListSelectionMemory = next;
		}

		let hasUpdatedPanelMemory = false;
		const nextPanelMemory = { ...this.dataListSelectionPanelMemory };
		if (key in nextPanelMemory) {
			delete nextPanelMemory[key];
			hasUpdatedPanelMemory = true;
		}
		if (hasUpdatedPanelMemory) {
			this.dataListSelectionPanelMemory = nextPanelMemory;
		}
	},
};
